import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  TablePagination,
  TableContainer
} from "@mui/material";
import axios from "axios";
import SearchIcon from "@mui/icons-material/Search";
import FactCheckIcon from "@mui/icons-material/FactCheck";

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchText,setSearchText] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchLogs();
  }, [selectedModule,selectedAction,page, rowsPerPage,search]);

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API}/audit/logs`, {
      params: {
        page,
        limit: rowsPerPage,
        search:search,
        module: selectedModule,
        action: selectedAction
      },
    });

      setLogs(res.data.data || []);
      setTotal(res.data.total);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };


  const getChipStyle = (action) => {
  switch (action?.toUpperCase()) {
    case "ADD":
      return {
        bgcolor: "#e3f5e4",
        color: "#2E7D32",
        fontWeight: 600,
      };

    case "UPDATE":
      return {
        bgcolor: "#d7eaf8",
        color: "#1565C0",
        fontWeight: 600,
      };

    case "DELETE":
      return {
        bgcolor: "#fae1e5",
        color: "#C62828",
        fontWeight: 600,
      };
    case "CANCELLED":
      return {
        bgcolor: "#fae0e4",
        color: "#d14825",
        fontWeight: 600,
      };

    case "ASSIGN":
      return {
        bgcolor: "#efdcf1",
        color: "#7B1FA2",
        fontWeight: 600,
      };

    case "LOGOUT":
      return {
        bgcolor: "#dde8f0",
        color: "#455A64",
        fontWeight: 600,
      };

    default:
      return {
        bgcolor: "#f5f3e7",
        color: "#f1da58",
        fontWeight: 600,
      };
  }
};

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
      <Box sx={{
          p: 3,
          ml: { md: "300px" },
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
>  
      <Typography
          variant="h5"
          fontWeight={700}
          display="flex"
          alignItems="center"
          gap={1}
          mb={3}
        >
          <FactCheckIcon color="primary" />
          Activity Logs
        </Typography>
      <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={2}
      mb={3}
      alignItems="center"
    >
  <TextField
    size="small"
    placeholder="Search by user..."
    value={searchText}
    onChange={(e) => setSearchText(e.target.value)}
    sx={{ width: 250 }}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon />
        </InputAdornment>
      ),
    }}
  />
  
  <FormControl size="small" sx={{ width: 180 }}>
    <InputLabel>Module</InputLabel>
    <Select
      value={selectedModule}
      label="Module"
      onChange={(e) => setSelectedModule(e.target.value)}
      MenuProps={{
    disableScrollLock: true,
  }}

    >
      <MenuItem value="">All</MenuItem>
      <MenuItem value="Medicine">Medicine</MenuItem>
      <MenuItem value="Diagnostic">Diagnostic</MenuItem>
      <MenuItem value="Nursing & Physio">Nursing & Physiotherapy</MenuItem>
    </Select>
  </FormControl>

  <FormControl size="small" sx={{ width: 180 }}>
    <InputLabel>Action</InputLabel>
    <Select
      value={selectedAction}
      label="Action"
      onChange={(e) => setSelectedAction(e.target.value)}
      MenuProps={{
    disableScrollLock: true,
  }} >
      <MenuItem value="">All</MenuItem>
      <MenuItem value="ADD">ADD</MenuItem>
      <MenuItem value="UPDATE">UPDATE</MenuItem>
      <MenuItem value="DELETE">DELETE</MenuItem>
      <MenuItem value="CANCELLED">CANCELLED</MenuItem>
    </Select>
  </FormControl>

  <Button
    variant="contained"
    onClick={()=>setSearch(searchText)}
  >
    Search
  </Button>

  <Button
    variant="outlined"
    onClick={() => {
      setSearch("");
      setSelectedModule("");
      setSelectedAction("");
      setSearchText("")
    }}
  >
    Reset
  </Button>
</Stack>

      <Paper
  elevation={2}
  sx={{
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  }}
>
  <TableContainer
    sx={{
      flex: 1,
      overflow: "auto",
    }}
  >
        <Table stickyHeader >
          <TableHead  sx={{
            "& .MuiTableCell-head": {
              bgcolor: "#006D77", // your desired color
              color: "#fff",
              fontWeight: 700,
              borderBottom: "1px solid #00545C",
            },
            }}>
            <TableRow>
              <TableCell sx={{ width: "20%" }}><b>Date</b></TableCell>
              <TableCell sx={{ width: "12%" }}><b>Admin</b></TableCell>
              <TableCell sx={{ width: "10%" }}><b>Module</b></TableCell>
              <TableCell sx={{ width: "10%" }}><b>Action</b></TableCell>
              <TableCell sx={{ width: "18%" }}><b>Entity</b></TableCell>
              <TableCell sx={{ width: "30%" }}><b>Changes</b></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>
                  {new Date(log.created_at).toLocaleString()}
                </TableCell>

                <TableCell>{log.admin_name}</TableCell>

                <TableCell>{log.module}</TableCell>

                <TableCell>
                  <Chip
                    size="small"
                    label={log.action}
                    sx={getChipStyle(log.action)}
                  />
                </TableCell>

                <TableCell>{log.entity_name}</TableCell>
                
 {log.action === "ADD" && (
    <TableCell>
        <Typography color="success.main">
            {log.module} <b>{log.entity_name}</b> was created.
        </Typography></TableCell>
    )}

    {log.action === "DELETE" && (
        <TableCell> <Typography color="error.main">
            {log.module} <b>{log.entity_name}</b> was deleted.
        </Typography></TableCell>
    )}
    {log.action === "ASSIGN" && (
        <TableCell> <Typography color="success.main">
            {log.entity_id} assigned to <b>{log.entity_name}</b>
        </Typography></TableCell>
    )}
    {(log.action === "UPDATE" || log.action === "CANCELLED") && (
                <TableCell>
                  {Object.entries(log.changes || {}).map(([field, value]) => (
                    <Box key={field} mb={1}>
                      <Typography variant="body2">
                        <b>{field}</b>
                      </Typography>

                      <Typography
                        variant="caption"
                        color="error"
                        display="block"
                      >
                        Old: {JSON.stringify(value.old)}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="success.main"
                        display="block"
                      >
                        New: {JSON.stringify(value.new)}
                      </Typography>
                    </Box>
                  ))}
                </TableCell>)}
              </TableRow>
            ))}

            {logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No logs found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
          </TableContainer>
        <TablePagination
  component="div"
  count={total}
  page={page - 1} // MUI is 0-based
  onPageChange={(event, newPage) => {
    setPage(newPage + 1);
  }}
  rowsPerPage={rowsPerPage}
  onRowsPerPageChange={(event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  }}
  rowsPerPageOptions={[10, 20, 50, 100]}
/>
      </Paper>
    </Box>
  );
}